import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { useToast } from "@/hooks/use-toast";
import { Mail, Bell, Leaf, Gift, ShoppingBag, Settings, Check, Loader2, AlertCircle } from "lucide-react";
import { motion } from "framer-motion";
import { apiRequest } from "@/lib/queryClient";

// Validation schemas
const emailLookupSchema = z.object({
  email: z.string().email("Please enter a valid email address"),
});

const preferencesSchema = z.object({
  marketing: z.boolean(),
  careReminders: z.boolean(),
  orderUpdates: z.boolean(),
  biomePreference: z.string().optional(),
});

type EmailLookupData = z.infer<typeof emailLookupSchema>;
type PreferencesData = z.infer<typeof preferencesSchema>;

interface SubscriberPreferences {
  email: string;
  status: string;
  marketing: boolean;
  careReminders: boolean;
  orderUpdates: boolean;
  biomePreference?: string;
  tags: string[];
}

interface PreferenceCenterProps {
  email?: string;
  onComplete?: () => void;
  className?: string;
}

export function PreferenceCenter({
  email: initialEmail,
  onComplete,
  className = "",
}: PreferenceCenterProps) {
  const [email, setEmail] = useState(initialEmail || "");
  const [isLookingUp, setIsLookingUp] = useState(!initialEmail);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const emailForm = useForm<EmailLookupData>({
    resolver: zodResolver(emailLookupSchema),
    defaultValues: { email: initialEmail || "" },
  });

  const preferencesForm = useForm<PreferencesData>({
    resolver: zodResolver(preferencesSchema),
    defaultValues: {
      marketing: true,
      careReminders: true,
      orderUpdates: true,
      biomePreference: "",
    },
  });

  // Fetch preferences when email is set
  const {
    data: preferences,
    isLoading: isLoadingPreferences,
    error: preferencesError,
    refetch: refetchPreferences,
  } = useQuery({
    queryKey: ["newsletter-preferences", email],
    queryFn: async () => {
      const response = await fetch(`/api/newsletter/preferences?email=${encodeURIComponent(email)}`);
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || "Failed to load preferences");
      }
      const data = await response.json();
      return data.preferences as SubscriberPreferences;
    },
    enabled: !!email && !isLookingUp,
    retry: false,
  });

  // Update form when preferences are loaded
  useEffect(() => {
    if (preferences) {
      preferencesForm.reset({
        marketing: preferences.marketing,
        careReminders: preferences.careReminders,
        orderUpdates: preferences.orderUpdates,
        biomePreference: preferences.biomePreference || "",
      });
    }
  }, [preferences, preferencesForm]);

  // Update preferences mutation
  const updateMutation = useMutation({
    mutationFn: async (data: PreferencesData) => {
      const response = await apiRequest("POST", "/api/newsletter/preferences", {
        email,
        ...data,
      });
      return response.json();
    },
    onSuccess: () => {
      toast({
        title: "Preferences Updated",
        description: "Your email preferences have been saved.",
      });
      queryClient.invalidateQueries({ queryKey: ["newsletter-preferences", email] });
      onComplete?.();
    },
    onError: (error: Error) => {
      toast({
        title: "Update Failed",
        description: error.message || "Please try again later.",
        variant: "destructive",
      });
    },
  });

  // Unsubscribe mutation
  const unsubscribeMutation = useMutation({
    mutationFn: async () => {
      const response = await apiRequest("POST", "/api/newsletter/unsubscribe", { email });
      return response.json();
    },
    onSuccess: () => {
      toast({
        title: "Unsubscribed",
        description: "You have been unsubscribed from all emails.",
      });
      queryClient.invalidateQueries({ queryKey: ["newsletter-preferences", email] });
      setIsLookingUp(true);
      setEmail("");
    },
    onError: (error: Error) => {
      toast({
        title: "Unsubscribe Failed",
        description: error.message || "Please try again later.",
        variant: "destructive",
      });
    },
  });

  const handleEmailLookup = (data: EmailLookupData) => {
    setEmail(data.email);
    setIsLookingUp(false);
  };

  const handlePreferencesSubmit = (data: PreferencesData) => {
    updateMutation.mutate(data);
  };

  const handleUnsubscribeAll = () => {
    if (confirm("Are you sure you want to unsubscribe from all emails? You can resubscribe at any time.")) {
      unsubscribeMutation.mutate();
    }
  };

  // Email lookup form
  if (isLookingUp) {
    return (
      <Card className={className}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="w-5 h-5" />
            Email Preferences
          </CardTitle>
          <CardDescription>
            Enter your email address to manage your subscription preferences.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={emailForm.handleSubmit(handleEmailLookup)} className="space-y-4">
            <div>
              <Label htmlFor="lookup-email">Email Address</Label>
              <Input
                {...emailForm.register("email")}
                id="lookup-email"
                type="email"
                placeholder="you@example.com"
                className={emailForm.formState.errors.email ? "border-destructive" : ""}
              />
              {emailForm.formState.errors.email && (
                <p className="text-destructive text-xs mt-1">
                  {emailForm.formState.errors.email.message}
                </p>
              )}
            </div>
            <Button type="submit" className="w-full">
              <Mail className="w-4 h-4 mr-2" />
              Find My Preferences
            </Button>
          </form>
        </CardContent>
      </Card>
    );
  }

  // Loading state
  if (isLoadingPreferences) {
    return (
      <Card className={className}>
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  // Error state - subscriber not found
  if (preferencesError) {
    return (
      <Card className={className}>
        <CardContent className="py-8">
          <div className="flex flex-col items-center text-center">
            <AlertCircle className="w-12 h-12 text-muted-foreground mb-4" />
            <h3 className="font-medium text-lg mb-2">Email Not Found</h3>
            <p className="text-muted-foreground mb-4">
              We couldn't find a subscription for {email}.
            </p>
            <Button
              variant="outline"
              onClick={() => {
                setIsLookingUp(true);
                setEmail("");
              }}
            >
              Try Another Email
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Preferences form
  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Settings className="w-5 h-5" />
          Email Preferences
        </CardTitle>
        <CardDescription>
          Manage your subscription preferences for {email}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={preferencesForm.handleSubmit(handlePreferencesSubmit)} className="space-y-6">
          {/* Email Types Section */}
          <div>
            <h4 className="font-medium mb-4">Email Types</h4>
            <div className="space-y-4">
              {/* Marketing Emails */}
              <motion.div
                className="flex items-start justify-between gap-4 p-4 rounded-lg border"
                whileHover={{ backgroundColor: "rgba(0,0,0,0.02)" }}
              >
                <div className="flex gap-3">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Gift className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <Label htmlFor="marketing" className="font-medium">
                      Marketing & Promotions
                    </Label>
                    <p className="text-sm text-muted-foreground mt-1">
                      New products, special offers, and exclusive discounts
                    </p>
                  </div>
                </div>
                <Switch
                  id="marketing"
                  checked={preferencesForm.watch("marketing")}
                  onCheckedChange={(checked) =>
                    preferencesForm.setValue("marketing", checked)
                  }
                />
              </motion.div>

              {/* Care Reminders */}
              <motion.div
                className="flex items-start justify-between gap-4 p-4 rounded-lg border"
                whileHover={{ backgroundColor: "rgba(0,0,0,0.02)" }}
              >
                <div className="flex gap-3">
                  <div className="w-10 h-10 rounded-full bg-green-500/10 flex items-center justify-center flex-shrink-0">
                    <Leaf className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <Label htmlFor="careReminders" className="font-medium">
                      Care Tips & Reminders
                    </Label>
                    <p className="text-sm text-muted-foreground mt-1">
                      Terrarium care tips, seasonal advice, and maintenance reminders
                    </p>
                  </div>
                </div>
                <Switch
                  id="careReminders"
                  checked={preferencesForm.watch("careReminders")}
                  onCheckedChange={(checked) =>
                    preferencesForm.setValue("careReminders", checked)
                  }
                />
              </motion.div>

              {/* Order Updates */}
              <motion.div
                className="flex items-start justify-between gap-4 p-4 rounded-lg border"
                whileHover={{ backgroundColor: "rgba(0,0,0,0.02)" }}
              >
                <div className="flex gap-3">
                  <div className="w-10 h-10 rounded-full bg-blue-500/10 flex items-center justify-center flex-shrink-0">
                    <ShoppingBag className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <Label htmlFor="orderUpdates" className="font-medium">
                      Order Updates
                    </Label>
                    <p className="text-sm text-muted-foreground mt-1">
                      Order confirmations, shipping updates, and delivery notifications
                    </p>
                  </div>
                </div>
                <Switch
                  id="orderUpdates"
                  checked={preferencesForm.watch("orderUpdates")}
                  onCheckedChange={(checked) =>
                    preferencesForm.setValue("orderUpdates", checked)
                  }
                />
              </motion.div>
            </div>
          </div>

          <Separator />

          {/* Biome Preference Section */}
          <div>
            <h4 className="font-medium mb-4">Content Preferences</h4>
            <div className="space-y-3">
              <Label>Preferred Terrarium Style</Label>
              <p className="text-sm text-muted-foreground mb-3">
                Help us personalize your content by selecting your favorite biome.
              </p>
              <RadioGroup
                value={preferencesForm.watch("biomePreference") || ""}
                onValueChange={(value) =>
                  preferencesForm.setValue("biomePreference", value)
                }
                className="grid grid-cols-2 gap-3"
              >
                <div className="flex items-center space-x-2 p-3 border rounded-lg">
                  <RadioGroupItem value="forest" id="forest" />
                  <Label htmlFor="forest" className="flex items-center gap-2 cursor-pointer">
                    <span className="text-lg">🌲</span> Forest
                  </Label>
                </div>
                <div className="flex items-center space-x-2 p-3 border rounded-lg">
                  <RadioGroupItem value="desert" id="desert" />
                  <Label htmlFor="desert" className="flex items-center gap-2 cursor-pointer">
                    <span className="text-lg">🏜️</span> Desert
                  </Label>
                </div>
                <div className="flex items-center space-x-2 p-3 border rounded-lg">
                  <RadioGroupItem value="tropical" id="tropical" />
                  <Label htmlFor="tropical" className="flex items-center gap-2 cursor-pointer">
                    <span className="text-lg">🌴</span> Tropical
                  </Label>
                </div>
                <div className="flex items-center space-x-2 p-3 border rounded-lg">
                  <RadioGroupItem value="" id="all" />
                  <Label htmlFor="all" className="flex items-center gap-2 cursor-pointer">
                    <span className="text-lg">🌿</span> All Styles
                  </Label>
                </div>
              </RadioGroup>
            </div>
          </div>

          <Separator />

          {/* Action Buttons */}
          <div className="flex flex-col gap-3">
            <Button
              type="submit"
              disabled={updateMutation.isPending}
              className="w-full"
            >
              {updateMutation.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Check className="w-4 h-4 mr-2" />
                  Save Preferences
                </>
              )}
            </Button>

            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                setIsLookingUp(true);
                setEmail("");
              }}
              className="text-muted-foreground"
            >
              Use Different Email
            </Button>
          </div>

          <Separator />

          {/* Unsubscribe Section */}
          <div className="text-center">
            <p className="text-sm text-muted-foreground mb-3">
              Want to stop receiving all emails from us?
            </p>
            <Button
              type="button"
              variant="destructive"
              size="sm"
              onClick={handleUnsubscribeAll}
              disabled={unsubscribeMutation.isPending}
            >
              {unsubscribeMutation.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Unsubscribing...
                </>
              ) : (
                "Unsubscribe from All"
              )}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
